import { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

// ── CONFIGURE THESE TWO VALUES ────────────────────────────────────
// Get from: Supabase Dashboard → Project Settings → API
const SUPABASE_URL  = "https://utaenhufgxrlpuhizqny.supabase.co";   // e.g. https://xxxx.supabase.co
const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0YWVuaHVmZ3hybHB1aGl6cW55Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE3MTk3MzYsImV4cCI6MjA4NzI5NTczNn0.00RYEwD7TfxNT7PeLvoHskDrA0O5kJjnh0jIlGxVfTY";
// ─────────────────────────────────────────────────────────────────

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);
const MAX_ATTEMPTS = 2;

// ── Styles ────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:#0f1117; --surface:#181c27; --surface2:#1f2436; --border:#2a3047;
    --amber:#f5a623; --amber-dim:#a06c12; --green:#2ecc71; --red:#e74c3c;
    --blue:#4a9eff; --text:#e8eaf0; --muted:#6b7394;
    --mono:'IBM Plex Mono',monospace; --sans:'IBM Plex Sans',sans-serif;
  }
  body { background:var(--bg); color:var(--text); font-family:var(--sans); min-height:100vh; }
  .app { max-width:480px; margin:0 auto; min-height:100vh; display:flex; flex-direction:column; }

  .header { background:var(--surface); border-bottom:1px solid var(--border); padding:16px 20px 12px; position:sticky; top:0; z-index:100; }
  .header-top { display:flex; justify-content:space-between; align-items:center; }
  .logo { font-family:var(--mono); font-size:13px; font-weight:700; letter-spacing:0.12em; color:var(--amber); text-transform:uppercase; }
  .clock { font-family:var(--mono); font-size:13px; color:var(--muted); }
  .window-badge { margin-top:10px; display:inline-flex; align-items:center; gap:8px; background:var(--surface2); border:1px solid var(--border); border-radius:6px; padding:6px 12px; font-family:var(--mono); font-size:11px; font-weight:600; letter-spacing:0.08em; }
  .window-dot { width:7px; height:7px; border-radius:50%; background:var(--green); animation:pulse 2s infinite; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  .window-dot.off { background:var(--muted); animation:none; }

  .tabs { display:flex; border-bottom:1px solid var(--border); background:var(--surface); }
  .tab { flex:1; padding:12px 8px; text-align:center; font-family:var(--mono); font-size:11px; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; color:var(--muted); cursor:pointer; border:none; background:none; border-bottom:2px solid transparent; transition:all .2s; }
  .tab.active { color:var(--amber); border-bottom-color:var(--amber); }
  .tab-count { display:inline-flex; align-items:center; justify-content:center; background:var(--amber); color:#000; border-radius:10px; font-size:10px; font-weight:700; min-width:18px; height:18px; padding:0 5px; margin-left:6px; }
  .tab-count.zero { background:var(--surface2); color:var(--muted); }

  .content { flex:1; padding:16px; display:flex; flex-direction:column; gap:12px; overflow-y:auto; padding-bottom:80px; }

  .section-label { font-family:var(--mono); font-size:10px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--muted); padding:4px 0 8px; border-bottom:1px solid var(--border); margin-bottom:4px; display:flex; justify-content:space-between; align-items:center; }

  .task-card { background:var(--surface); border:1px solid var(--border); border-radius:10px; overflow:hidden; animation:slideIn .2s ease; }
  @keyframes slideIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
  .task-card.urgent { border-left:3px solid var(--amber); }
  .task-card.estimate { border-left:3px solid var(--blue); }
  .task-card.other { border-left:3px solid var(--muted); }
  .task-card.done { opacity:0.45; }

  .task-header { padding:14px 16px 10px; }
  .task-top { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
  .task-name { font-family:var(--sans); font-size:15px; font-weight:600; color:var(--text); }
  .task-type-badge { font-family:var(--mono); font-size:9px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; padding:3px 7px; border-radius:4px; white-space:nowrap; flex-shrink:0; }
  .badge-lead { background:rgba(245,166,35,.15); color:var(--amber); }
  .badge-estimate { background:rgba(74,158,255,.15); color:var(--blue); }
  .badge-complaint { background:rgba(231,76,60,.15); color:var(--red); }
  .badge-cancellation { background:rgba(231,76,60,.2); color:var(--red); }
  .badge-billing { background:rgba(107,115,148,.2); color:var(--muted); }
  .badge-service { background:rgba(46,204,113,.15); color:var(--green); }
  .badge-other { background:rgba(107,115,148,.15); color:var(--muted); }

  .task-meta { margin-top:6px; display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
  .task-meta-item { font-family:var(--mono); font-size:11px; color:var(--muted); display:flex; align-items:center; gap:4px; }
  .task-meta-item span { color:var(--text); }
  .company-tag { font-family:var(--mono); font-size:10px; font-weight:700; padding:2px 6px; border-radius:3px; letter-spacing:0.06em; }
  .tag-agl { background:rgba(46,204,113,.15); color:var(--green); }
  .tag-ggl { background:rgba(74,158,255,.15); color:var(--blue); }
  .task-service { margin-top:8px; padding:8px 12px; background:var(--surface2); border-radius:6px; font-size:13px; color:var(--text); line-height:1.4; }
  .task-notes { margin-top:6px; padding:0 16px 4px; font-size:12px; color:var(--muted); font-style:italic; line-height:1.4; white-space:pre-line; }

  .attempt-row { padding:8px 16px; display:flex; align-items:center; gap:8px; }
  .attempt-label { font-family:var(--mono); font-size:10px; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; }
  .pips { display:flex; gap:5px; }
  .pip { width:22px; height:8px; border-radius:3px; background:var(--border); }
  .pip.tried { background:var(--amber-dim); }
  .pip.max { background:var(--red); opacity:.7; }

  .task-actions { padding:10px 16px 14px; display:flex; gap:8px; }
  .btn { flex:1; padding:10px 8px; border-radius:8px; border:none; font-family:var(--mono); font-size:11px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; transition:all .15s; text-align:center; }
  .btn:active { transform:scale(.97); }
  .btn:disabled { opacity:.4; cursor:not-allowed; }
  .btn-reached { background:var(--green); color:#000; }
  .btn-noanswer { background:var(--surface2); color:var(--muted); border:1px solid var(--border); }
  .btn-noanswer:hover:not(:disabled) { border-color:var(--amber); color:var(--amber); }
  .btn-closed { background:var(--amber); color:#000; }

  .add-btn { position:fixed; bottom:24px; right:50%; transform:translateX(50%); right:calc(50% - 216px); width:52px; height:52px; border-radius:50%; background:var(--amber); color:#000; border:none; font-size:26px; cursor:pointer; box-shadow:0 4px 20px rgba(245,166,35,.4); display:flex; align-items:center; justify-content:center; transition:all .2s; z-index:200; }
  .add-btn:hover { transform:scale(1.08); box-shadow:0 6px 28px rgba(245,166,35,.5); }

  .modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.7); z-index:300; display:flex; align-items:flex-end; justify-content:center; animation:fadeIn .2s; }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .modal { background:var(--surface); border-radius:16px 16px 0 0; width:100%; max-width:480px; padding:24px 20px 40px; border-top:1px solid var(--border); animation:slideUp .25s ease; max-height:90vh; overflow-y:auto; }
  @keyframes slideUp { from{transform:translateY(100%)} to{transform:none} }
  .modal-title { font-family:var(--mono); font-size:13px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--amber); margin-bottom:20px; }
  .form-group { margin-bottom:16px; }
  .form-label { font-family:var(--mono); font-size:10px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); display:block; margin-bottom:6px; }
  .form-input,.form-select,.form-textarea { width:100%; padding:11px 14px; border-radius:8px; background:var(--surface2); border:1px solid var(--border); color:var(--text); font-family:var(--sans); font-size:14px; outline:none; transition:border-color .15s; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:var(--amber); }
  .form-select option { background:var(--surface2); }
  .form-textarea { resize:vertical; min-height:80px; }
  .form-row { display:flex; gap:10px; }
  .form-row .form-group { flex:1; }
  .modal-actions { display:flex; gap:10px; margin-top:20px; }
  .btn-primary { flex:2; padding:13px; background:var(--amber); color:#000; border:none; border-radius:8px; font-family:var(--mono); font-size:12px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; }
  .btn-cancel { flex:1; padding:13px; background:var(--surface2); color:var(--muted); border:1px solid var(--border); border-radius:8px; font-family:var(--mono); font-size:12px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; }

  .empty { text-align:center; padding:48px 24px; }
  .empty-icon { font-size:40px; margin-bottom:12px; }
  .empty-title { font-family:var(--mono); font-size:13px; font-weight:700; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; }
  .empty-sub { font-size:13px; color:var(--muted); margin-top:6px; }

  .stats-bar { display:flex; gap:1px; background:var(--border); border-radius:8px; overflow:hidden; margin-bottom:4px; }
  .stat-cell { flex:1; background:var(--surface); padding:10px 8px; text-align:center; }
  .stat-num { font-family:var(--mono); font-size:18px; font-weight:700; color:var(--amber); }
  .stat-label { font-family:var(--mono); font-size:9px; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); margin-top:2px; }

  .loading { text-align:center; padding:40px; font-family:var(--mono); font-size:12px; color:var(--muted); letter-spacing:0.08em; }
  .error-bar { background:rgba(231,76,60,.15); border:1px solid var(--red); border-radius:8px; padding:10px 14px; font-family:var(--mono); font-size:11px; color:var(--red); margin-bottom:8px; }

  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:var(--bg); }
  ::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px; }
`;

// ── Helpers ───────────────────────────────────────────────────────

const TASK_TYPES = ["lead","estimate","complaint","cancellation","billing","service","other"];
const TYPE_LABELS = { lead:"New Lead", estimate:"Estimate F/U", complaint:"Complaint", cancellation:"Cancellation", billing:"Billing", service:"Svc Change", other:"Other" };
const TYPE_PRIORITY = { lead:0, estimate:1, cancellation:2, complaint:3, billing:4, service:5, other:6 };

function badgeLabel(type) { return TYPE_LABELS[type] || type; }
function badgeClass(type) { return `task-type-badge badge-${type}`; }
function cardClass(task) {
  if (task.completed) return "task-card done";
  if (task.type === "lead") return "task-card urgent";
  if (task.type === "estimate") return "task-card estimate";
  return "task-card other";
}
function getWindow() {
  const h = new Date().getHours(), m = new Date().getMinutes();
  if (h === 11 || (h === 10 && m >= 55)) return "morning";
  if (h === 17 || (h === 16 && m >= 55)) return "evening";
  return "off";
}
function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}) + " · " + d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
}
function nowISO() { return new Date().toISOString(); }
function appendNote(existing, note) {
  const ts = new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}) + " · " + new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"});
  return [existing, `${note} – ${ts}`].filter(Boolean).join("\n");
}

// ── App ───────────────────────────────────────────────────────────

export default function FieldOps() {
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [tab, setTab]           = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving]     = useState(null); // task id being saved
  const [clock, setClock]       = useState(new Date());
  const [form, setForm]         = useState({ type:"lead", company:"AGL", name:"", phone:"", service:"", notes:"", source:"" });

  // Clock
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Load tasks
  const loadTasks = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from("fieldops_tasks")
        .select("*")
        .order("created_at", { ascending: true });
      if (err) throw err;
      setTasks(data || []);
      setError(null);
    } catch (e) {
      setError("Failed to load tasks. Check Supabase config.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("fieldops_tasks_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "fieldops_tasks" }, () => loadTasks())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [loadTasks]);

  // Derived lists
  const activeTasks    = tasks.filter(t => !t.completed && t.attempts < MAX_ATTEMPTS);
  const exhaustedTasks = tasks.filter(t => !t.completed && t.attempts >= MAX_ATTEMPTS);
  const doneTasks      = tasks.filter(t => t.completed);

  const prioritized = [...activeTasks].sort((a, b) => {
    const po = TYPE_PRIORITY, ta = po[a.type] ?? 99, tb = po[b.type] ?? 99;
    return ta !== tb ? ta - tb : new Date(a.created_at) - new Date(b.created_at);
  });

  // Actions
  async function attempt(task) {
    setSaving(task.id);
    const newAttempts = task.attempts + 1;
    const newNotes = appendNote(task.notes, "No answer");
    const { error: err } = await supabase.from("fieldops_tasks").update({ attempts: newAttempts, notes: newNotes }).eq("id", task.id);
    if (err) setError("Update failed.");
    await loadTasks();
    setSaving(null);
  }

  async function reached(task) {
    setSaving(task.id);
    const { error: err } = await supabase.from("fieldops_tasks").update({
      completed: true,
      completed_at: nowISO(),
      notes: appendNote(task.notes, "Reached"),
    }).eq("id", task.id);
    if (err) setError("Update failed.");
    await loadTasks();
    setSaving(null);
  }

  async function closed(task) {
    setSaving(task.id);
    const { error: err } = await supabase.from("fieldops_tasks").update({
      completed: true,
      completed_at: nowISO(),
      notes: appendNote(task.notes, "CLOSED — SALE"),
    }).eq("id", task.id);
    if (err) setError("Update failed.");
    await loadTasks();
    setSaving(null);
  }

  async function addTask(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    const { error: err } = await supabase.from("fieldops_tasks").insert({
      type: form.type, company: form.company, name: form.name.trim(),
      phone: form.phone.trim(), service: form.service.trim(),
      notes: form.notes.trim(), source: form.source.trim(),
      attempts: 0, completed: false,
    });
    if (err) { setError("Failed to add task."); return; }
    setShowModal(false);
    setForm({ type:"lead", company:"AGL", name:"", phone:"", service:"", notes:"", source:"" });
    await loadTasks();
  }

  const win = getWindow();
  const displayTasks = tab === "active" ? prioritized : tab === "exhausted" ? exhaustedTasks : doneTasks;
  const tabCounts = { active: prioritized.length, exhausted: exhaustedTasks.length, done: doneTasks.length };

  return (
    <>
      <style>{STYLES}</style>
      <div className="app">

        {/* Header */}
        <div className="header">
          <div className="header-top">
            <div className="logo">FieldOps</div>
            <div className="clock">{clock.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
          </div>
          <div className="window-badge">
            <div className={`window-dot ${win === "off" ? "off" : ""}`} />
            {win === "morning" ? "11AM CALL WINDOW ACTIVE" : win === "evening" ? "5PM CALL WINDOW ACTIVE" : "NEXT: 11AM – 12PM"}
          </div>
        </div>

        {/* Stats */}
        <div style={{padding:"12px 16px 0"}}>
          <div className="stats-bar">
            {[
              [tasks.filter(t=>t.type==="lead"&&!t.completed&&t.attempts<MAX_ATTEMPTS).length, "New Leads"],
              [tasks.filter(t=>t.type==="estimate"&&!t.completed&&t.attempts<MAX_ATTEMPTS).length, "Estimates"],
              [tasks.filter(t=>!["lead","estimate"].includes(t.type)&&!t.completed&&t.attempts<MAX_ATTEMPTS).length, "Other"],
              [doneTasks.length, "Done Today"],
            ].map(([n, label]) => (
              <div key={label} className="stat-cell">
                <div className="stat-num">{n}</div>
                <div className="stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          {[["active","QUEUE"],["exhausted","EXPIRED"],["done","DONE"]].map(([key,label]) => (
            <button key={key} className={`tab ${tab===key?"active":""}`} onClick={()=>setTab(key)}>
              {label}<span className={`tab-count ${tabCounts[key]===0?"zero":""}`}>{tabCounts[key]}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="content">
          {error && <div className="error-bar">⚠ {error}</div>}
          {loading && <div className="loading">LOADING TASKS...</div>}

          {!loading && tab === "active" && prioritized.length > 0 && (
            <div className="section-label"><span>Call Priority Order</span><span>{prioritized.length} remaining</span></div>
          )}

          {!loading && displayTasks.length === 0 && (
            <div className="empty">
              <div className="empty-icon">{tab==="done"?"✓":tab==="exhausted"?"⊘":"📋"}</div>
              <div className="empty-title">{tab==="active"?"Queue Clear":tab==="exhausted"?"None Expired":"Nothing Done Yet"}</div>
              <div className="empty-sub">{tab==="active"?"No calls needed right now.":tab==="exhausted"?"All within attempt limit.":"Completed tasks appear here."}</div>
            </div>
          )}

          {!loading && displayTasks.map(task => {
            const busy = saving === task.id;
            return (
              <div key={task.id} className={cardClass(task)}>
                <div className="task-header">
                  <div className="task-top">
                    <div className="task-name">{task.name}</div>
                    <span className={badgeClass(task.type)}>{badgeLabel(task.type)}</span>
                  </div>
                  <div className="task-meta">
                    <span className={`company-tag tag-${task.company.toLowerCase()}`}>{task.company}</span>
                    {task.phone && <span className="task-meta-item">📞 <span>{task.phone}</span></span>}
                    {task.source && <span className="task-meta-item">via <span>{task.source}</span></span>}
                    <span className="task-meta-item" style={{fontSize:10}}>{formatTime(task.created_at)}</span>
                    {task.estimate_amount && <span className="task-meta-item">💲<span>${parseFloat(task.estimate_amount).toFixed(0)}/svc</span></span>}
                  </div>
                  {task.service && <div className="task-service">{task.service}</div>}
                </div>
                {task.notes ? <div className="task-notes">{task.notes}</div> : null}

                <div className="attempt-row">
                  <span className="attempt-label">Attempts</span>
                  <div className="pips">
                    {Array.from({length:MAX_ATTEMPTS}).map((_,i) => (
                      <div key={i} className={`pip ${i<task.attempts?(task.attempts>=MAX_ATTEMPTS?"max":"tried"):""}`} />
                    ))}
                  </div>
                  <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)",marginLeft:4}}>{task.attempts}/{MAX_ATTEMPTS}</span>
                </div>

                {!task.completed && (
                  <div className="task-actions">
                    <button className="btn btn-reached" disabled={busy} onClick={()=>reached(task)}>✓ Reached</button>
                    {task.type === "estimate" && (
                      <button className="btn btn-closed" disabled={busy} onClick={()=>closed(task)}>$ Closed</button>
                    )}
                    {task.attempts < MAX_ATTEMPTS && (
                      <button className="btn btn-noanswer" disabled={busy} onClick={()=>attempt(task)}>
                        {busy ? "..." : "No Answer"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button className="add-btn" onClick={()=>setShowModal(true)}>+</button>

        {showModal && (
          <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
            <div className="modal">
              <div className="modal-title">Add Task</div>
              <form onSubmit={addTask}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <select className="form-select" value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))}>
                      <option>AGL</option><option>GGL</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                      {TASK_TYPES.map(t=><option key={t} value={t}>{badgeLabel(t)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Name</label>
                  <input className="form-input" placeholder="Full name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" placeholder="(843) 555-0000" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Service / Details</label>
                  <textarea className="form-textarea" placeholder="Service plan, yard size, notes..." value={form.service} onChange={e=>setForm(f=>({...f,service:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <input className="form-input" placeholder="Angi, Website, Agent 2..." value={form.source} onChange={e=>setForm(f=>({...f,source:e.target.value}))} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={()=>setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Add to Queue</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
